import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, TrendingUp, Target, BarChart3, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { trackButtonClick, trackFormSubmit, trackConversion } from '../utils/analytics';
import mockData from '../mock';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    niche: '',
    objective: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Scroll animation refs
  const heroRef = useScrollAnimation();
  const servicesRef = useScrollAnimation();
  const whyUsRef = useScrollAnimation();
  const contactRef = useScrollAnimation();

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success("Success!", {
          description: data.message,
        });
        
        // Track successful form submission and conversion
        trackFormSubmit('contact_form', formData);
        trackConversion('lead_generation', 100);
        
        // Reset form
        setFormData({
          name: '',
          contact: '',
          niche: '',
          objective: ''
        });
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (error) {
      toast.error("Error", {
        description: error.message || "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: 'var(--bg-page)' }}>
      {/* Floating background elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute w-96 h-96 rounded-full opacity-5 floating-element"
          style={{ 
            background: 'radial-gradient(circle, var(--brand-primary), transparent)',
            left: `${mousePosition.x * 0.01}px`,
            top: `${mousePosition.y * 0.01}px`
          }}
        />
        <div 
          className="absolute w-64 h-64 rounded-full opacity-3 floating-element"
          style={{ 
            background: 'radial-gradient(circle, var(--brand-primary), transparent)',
            right: `${mousePosition.x * 0.005}px`,
            bottom: `${mousePosition.y * 0.005}px`,
            animationDelay: '-2s'
          }}
        />
      </div>

      {/* Hero Section */}
      <section className="py-32 pt-40 relative">
        <div className="container max-w-6xl mx-auto px-6">
          <div 
            ref={heroRef}
            className="max-w-4xl mx-auto text-center scroll-fade-in"
          >
            <div className="mb-6">
              <Sparkles 
                className="h-8 w-8 mx-auto mb-4 icon-hover floating-element" 
                style={{ color: 'var(--brand-primary)' }} 
              />
            </div>
            <h1 className="brand-display mb-8 text-glow" style={{ color: 'var(--text-primary)' }}>
              Advertising that brings profit
            </h1>
            <p className="body-large mb-10 max-w-3xl mx-auto opacity-90" style={{ color: 'var(--text-secondary)' }}>
              Launching campaigns in Telegram, Instagram and messengers that bring value. 
              No templates – only individual solutions.
            </p>
            <div className="scale-in">
              <button 
                className="btn-primary btn-animated px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 inline-flex items-center pulse-glow"
                style={{ 
                  backgroundColor: 'var(--brand-primary)',
                  color: 'var(--text-inverse)'
                }}
                onClick={() => {
                  trackButtonClick('Get Strategy CTA', 'hero');
                  document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <ArrowRight className="mr-2 h-5 w-5" />
                Get a strategy for my business
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20">
        <div className="container max-w-6xl mx-auto px-6">
          <div 
            ref={servicesRef}
            className="text-center mb-16 scroll-fade-in"
          >
            <h2 className="heading-2 mb-6 text-glow" style={{ color: 'var(--text-primary)' }}>Our Services</h2>
            <p className="body-large max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
              Complete digital marketing solutions tailored to your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-container">
            {mockData.services.overview.map((service, index) => (
              <div 
                key={service.id} 
                className={`service-card p-8 rounded-2xl border transition-all duration-500 hover:scale-105 ${
                  index % 2 === 0 ? 'scroll-slide-left' : 'scroll-slide-right'
                }`}
                style={{ 
                  backgroundColor: 'rgba(48, 47, 44, 0.8)',
                  borderColor: 'rgba(63, 72, 22, 0.3)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  animationDelay: `${index * 0.2}s`
                }}
              >
                <div className="mb-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center floating-element"
                    style={{ backgroundColor: 'rgba(217, 251, 6, 0.1)' }}
                  >
                    <CheckCircle className="h-6 w-6 icon-hover" style={{ color: 'var(--brand-primary)' }} />
                  </div>
                </div>
                <h3 className="heading-4 mb-6 text-glow" style={{ color: 'var(--text-primary)' }}>
                  {service.title}
                </h3>
                <ul className="space-y-3">
                  {service.subServices.map((subService, subIndex) => (
                    <li key={subIndex} className="flex items-start">
                      <CheckCircle className="h-4 w-4 mr-3 mt-1 flex-shrink-0 icon-hover" style={{ color: 'var(--brand-primary)' }} />
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{subService}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section 
        className="py-20 relative"
        style={{ 
          backgroundColor: 'rgba(48, 47, 44, 0.5)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)'
        }}
      >
        <div className="container max-w-6xl mx-auto px-6">
          <div 
            ref={whyUsRef}
            className="text-center mb-16 scroll-fade-in"
          >
            <h2 className="heading-2 mb-6 text-glow" style={{ color: 'var(--text-primary)' }}>Why Choose Us</h2>
            <p className="body-large max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
              {mockData.whyUs.intro}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 stagger-container">
            {mockData.whyUs.points.map((point, index) => {
              const IconComponent = index === 0 ? BarChart3 : index === 1 ? Target : TrendingUp;
              
              return (
                <div 
                  key={index} 
                  className="text-center p-8 rounded-2xl transition-all duration-500 hover:scale-105 scroll-fade-in"
                  style={{
                    backgroundColor: 'rgba(26, 28, 27, 0.6)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(217, 251, 6, 0.1)',
                    animationDelay: `${index * 0.2}s`
                  }}
                >
                  <div className="mb-6">
                    <div 
                      className="w-16 h-16 mx-auto rounded-full flex items-center justify-center floating-element"
                      style={{ backgroundColor: 'rgba(217, 251, 6, 0.1)' }}
                    >
                      <IconComponent className="h-8 w-8 icon-hover" style={{ color: 'var(--brand-primary)' }} />
                    </div>
                  </div>
                  <h3 className="heading-4 mb-4 text-glow" style={{ color: 'var(--text-primary)' }}>{point.title}</h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{point.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section id="contact-form" className="py-20">
        <div className="container max-w-6xl mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div 
              ref={contactRef}
              className="text-center mb-16 scroll-fade-in"
            >
              <h2 className="heading-2 mb-6 text-glow" style={{ color: 'var(--text-primary)' }}>
                Your business can sell more. Let us show you how.
              </h2>
              <p className="body-large max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                Get a personalized advertising strategy tailored to your business goals.
              </p>
            </div>

            <div 
              className="service-card p-10 rounded-2xl border scale-in"
              style={{ 
                backgroundColor: 'rgba(48, 47, 44, 0.9)',
                borderColor: 'rgba(63, 72, 22, 0.3)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)'
              }}
            >
              <form onSubmit={handleSubmit} className="space-y-6 stagger-container">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block body-small mb-2" style={{ color: 'var(--text-primary)' }}>
                      Name *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full p-4 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:scale-105"
                      style={{ 
                        backgroundColor: 'rgba(26, 28, 27, 0.8)',
                        borderColor: 'var(--border-medium)',
                        color: 'var(--text-primary)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                      placeholder="Your full name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="contact" className="block body-small mb-2" style={{ color: 'var(--text-primary)' }}>
                      Email or Telegram *
                    </label>
                    <input
                      id="contact"
                      name="contact"
                      type="text"
                      required
                      value={formData.contact}
                      onChange={handleInputChange}
                      className="w-full p-4 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:scale-105"
                      style={{ 
                        backgroundColor: 'rgba(26, 28, 27, 0.8)',
                        borderColor: 'var(--border-medium)',
                        color: 'var(--text-primary)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                      placeholder="email@example.com or @telegram"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="niche" className="block body-small mb-2" style={{ color: 'var(--text-primary)' }}>
                      Niche *
                    </label>
                    <input
                      id="niche"
                      name="niche"
                      type="text"
                      required
                      value={formData.niche}
                      onChange={handleInputChange}
                      className="w-full p-4 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:scale-105"
                      style={{ 
                        backgroundColor: 'rgba(26, 28, 27, 0.8)',
                        borderColor: 'var(--border-medium)',
                        color: 'var(--text-primary)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                      placeholder="E-commerce, SaaS, Services..."
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="objective" className="block body-small mb-2" style={{ color: 'var(--text-primary)' }}>
                      Advertising Objective *
                    </label>
                    <input
                      id="objective"
                      name="objective"
                      type="text"
                      required
                      value={formData.objective}
                      onChange={handleInputChange}
                      className="w-full p-4 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:scale-105"
                      style={{ 
                        backgroundColor: 'rgba(26, 28, 27, 0.8)',
                        borderColor: 'var(--border-medium)',
                        color: 'var(--text-primary)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                      placeholder="Increase sales, generate leads..."
                    />
                  </div>
                </div>

                <div className="text-center">
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="btn-primary btn-animated px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 inline-flex items-center pulse-glow"
                    style={{ 
                      backgroundColor: 'var(--brand-primary)',
                      color: 'var(--text-inverse)'
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <ArrowRight className="mr-2 h-5 w-5" />
                        Get a personalised offer
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;